import ChatSurfaceSwitcher from 'tripPlanner/components/Chat/ChatSurfaceSwitcher'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import clsx from 'clsx'
import { useIntl } from 'react-intl'
import { getElementTextDirection } from 'lib/dom/logicalScroll'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import ModalContext from 'contexts/ModalContext'
import ChatPanel from 'tripPlanner/assistant/components/AssistantChatPanel'
import MessagesList from 'tripPlanner/components/Chat/MessagesList'
import ChatMessageInput from 'tripPlanner/components/Chat/ChatMessageInput'
import ChatFileDropArea from 'tripPlanner/components/Chat/ChatFileDropArea'
import { isTripPlannerRegenerationCompatible } from 'tripPlanner/assistant/surfacePolicy'
import AddBookingModal from 'tripPlanner/components/Chat/AddBookingModal'
import ChatInputContextBar from '../components/ChatInputContextBar'
import AiContextTrace, { type TracePoint } from '../components/AiContextTrace'
import SalesAgentSummaryPanel from '../components/SalesAgentSummaryPanel'
import { ChatMessageActionsProvider, type OfferClickSearchParams } from 'tripPlanner/assistant/contexts/ChatMessageActionsContext'
import type { ChatMapOnMapData } from 'tripPlanner/assistant/mapTypes'
import type { OrderLinkOptions } from 'tripPlanner/assistant/runtime/chatStream'
import type { ComposerSubmitOptions } from '../hooks/useMessageContext'
import { useAddBookingsSubmit, type AddBookingsSend } from '../hooks/useAddBookingsSubmit'
import type { AddBookingItems } from '../hooks/useAddedBookingItemIds'
import type { LocationInfoData } from 'tripPlanner/components/Chat/Message'
import { useChatFiles } from '../contexts/ChatFilesContext'
import { InputWrapper } from '../styles/layoutStyles'
import type { HotelMapViewData, ExperienceMapViewData, MessageFeedbackReaction } from 'tripPlanner/api/types'
import { useClipboard } from 'tripPlanner/assistant/hooks/useClipboard'
import { useFeedback } from 'tripPlanner/assistant/hooks/useFeedback'
import { getChatErrorMessage, getChatErrorHeading } from '../utils/chatErrorMessages'
import { useTripPlannerEventBus, useEventSubscription, type ListingSearchDrawerRequest } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { useStaffRewind } from 'tripPlanner/assistant/contexts/StaffRewindContext'
import {
  buildAskAIContextForDay,
  buildAskAIContextForItem,
  buildAskAIContextForListRow,
} from '../utils/buildAskAIContext'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useChatScrollContext } from '../contexts/ChatScrollContext'
import { useChatStreamMessages } from '../contexts/ChatStreamMessagesContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { useMapInteraction } from '../contexts/MapInteractionContext'
import { buildPlaceDrawerOpenedActivityEntry } from './buildPlaceDrawerOpenedActivityEntry'
import { buildLocationSuggestionOpenedActivityEntry } from './buildLocationSuggestionOpenedActivityEntry'
import { buildMessageFeedbackActivityEntry } from './buildMessageFeedbackActivityEntry'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import BodyText from 'LuxKit/Typography/BodyText'
import { appendContextTag, hasContextLabel, WHATSAPP_HANDOFF_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'
import { getTextFromParts } from 'tripPlanner/utils/messageParts'
import { shouldDefaultSalesSummaryCollapsed } from '../utils/salesAgentSummary'
import noop from 'lib/function/noop'
import { useScheduleCallbackChatAction } from 'tripPlanner/components/Chat/hooks/useScheduleCallbackChatAction'
import FlightSearchDrawer from '../components/FlightSearchDrawer/FlightSearchDrawer'
import ChatCart from '../components/ChatCart'
import {
  useDrawerAskAIComposerContext,
  type ChatInputContext,
} from '../hooks/useDrawerAskAIComposerContext'
import { ChatMapLiveSlotProvider } from 'tripPlanner/components/Chat/ChatMapLiveSlotContext'
import GuestChatLoginPrompt from '../components/GuestChatLoginPrompt'
import useNavigateTo from 'hooks/useNavigateTo'
import { useAppSelector } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import { getItinerary } from 'tripPlanner/api'
import { CHAT_TRIP_PLANNER_NEW_PATH } from 'tripPlanner/config'
import { buildStagedChatPromptQuery } from 'tripPlanner/utils/stagedChatPrompt'
import { buildFailedChatRecoveryPrompt } from 'tripPlanner/utils/failedChatRecoveryPrompt'

const FAILED_MESSAGE_RETRY_STORAGE_PREFIX = 'tripPlanner:failedMessageRetried:'

function hasRetriedFailedMessage(messageId: string | undefined): boolean {
  if (!messageId) return false
  try {
    return window.sessionStorage.getItem(`${FAILED_MESSAGE_RETRY_STORAGE_PREFIX}${messageId}`) === '1'
  } catch {
    return false
  }
}

interface Props {
  chatId: number | undefined
  isLoggedIn: boolean
  isDevMode: boolean
  isSpoofed: boolean
  hiddenFromCustomer?: boolean
  spoofingAgentEmail?: string
  navigateToNewChatRoute: () => void
  handleSubmit: (message: string, options?: ComposerSubmitOptions) => Promise<void>
  handleMultiChoiceSelectionChange: (selection: Array<string>) => void
  activeMultiChoiceSelection: Array<string>
  prefilledSelectionTags?: Array<string>
  mobileSelectionText: string | undefined
  offerContextTag: { label: string; offerId: string } | undefined
  handleRemoveTag: (tag: string) => void
  onRemovePrefilledSelectionTag?: () => void
  addedBookingItemIds: ReadonlyArray<string>
  addBookingItems: AddBookingItems
  chatInputRef: React.RefObject<HTMLTextAreaElement | null>
  autoFocusChatInput?: boolean
  /**
   * Set by surfaces whose Timeline/List tab carries its own Ask AI composer, so the
   * hidden chat tab doesn't also swallow the highlight. The surface that owns the tabs
   * is the only thing that knows this — deriving it here would guess.
   */
  deferAskAIHighlightToLocalComposer?: boolean
  /** False while a surface keeps the chat mounted behind another tab, so the composer can't prompt unseen */
  isComposerVisible?: boolean
  dismissKeyboardOnSubmit?: boolean
  compactDisclaimer?: boolean
  enableMapActions?: boolean
  fitAvailableHeight?: boolean
  showAddedItineraryItemLink?: boolean
}

const DEFAULT_PREFILLED_SELECTION_TAGS: Array<string> = []

function ChatContainer({
  chatId,
  isLoggedIn,
  isDevMode,
  isSpoofed,
  hiddenFromCustomer = false,
  spoofingAgentEmail,
  navigateToNewChatRoute,
  handleSubmit,
  handleMultiChoiceSelectionChange,
  activeMultiChoiceSelection,
  prefilledSelectionTags = DEFAULT_PREFILLED_SELECTION_TAGS,
  mobileSelectionText,
  offerContextTag,
  handleRemoveTag,
  onRemovePrefilledSelectionTag = noop,
  addedBookingItemIds,
  addBookingItems,
  chatInputRef,
  autoFocusChatInput = true,
  deferAskAIHighlightToLocalComposer = false,
  isComposerVisible = true,
  dismissKeyboardOnSubmit = false,
  compactDisclaimer = false,
  enableMapActions = true,
  fitAvailableHeight = false,
  showAddedItineraryItemLink = false,
}: Props) {
  const intl = useIntl()
  const navigateTo = useNavigateTo()
  const { currentRegionCode } = useContext(GeoContext)
  const accessToken = useAppSelector(state => state.auth.accessToken)
  const eventBus = useTripPlannerEventBus()
  const { canStaffRewind } = useStaffRewind()
  const showModal = useContext(ModalContext)
  const { onCardHover } = useMapInteraction()
  const { files, setFiles } = useChatFiles()

  const { regenerateResponse: streamingRegenerate, sendPostAuthMessage, setFollowTail, submitMessage } = useChatActions()
  const { isStreaming, isStreamOpen, loadingState, isOnline, reasoningSummaries, toolCallInfo } = useChatStreamStatus()
  const { handleScroll: handleMessagesScroll, isAtBottom, hasContentOutOfView, enableFollowAndScrollToBottom, dynamicSpacerHeight, messagesWrapperRef } = useChatScrollContext()

  const {
    messages,
    filteredMessages,
    isChatError,
    chatError,
    userFeedbackMap,
    setUserFeedbackMap,
  } = useChatStreamMessages()

  const { itineraryData, isFlightCartMutationPending } = useItineraryContext()

  // ── Local state ──

  const [messageInputValue, setMessageInputValue] = useState('')
  const [chatInputContext, setChatInputContext] = useState<ChatInputContext | undefined>(undefined)
  const [contextTrace, setContextTrace] = useState<{ origin: TracePoint; key: number } | undefined>(undefined)
  const [retriedFailedMessageId, setRetriedFailedMessageId] = useState<string | undefined>(undefined)
  const contextBarRef = useRef<HTMLDivElement>(null)
  const traceKeyRef = useRef(0)
  const historyOverlayTouchStartYRef = useRef<number | undefined>(undefined)

  // ── Derived values ──

  const isLoading = loadingState.status === 'loadingHistory' || loadingState.status === 'preparingStream'
  const isListLoading = loadingState.isListLoading
  const isUpdatingItinerary = loadingState.isUpdatingItinerary
  const activeToolLoadingKey = loadingState.activeToolLoadingKey
  const shouldShowSalesSummary = isSpoofed && chatId !== undefined
  const shouldStartSalesSummaryCollapsed = shouldDefaultSalesSummaryCollapsed(spoofingAgentEmail)

  const messagesWithoutLocationEntities = useMemo(() => {
    return filteredMessages.map(msg => ({
      ...msg,
      parts: msg.parts.filter(part => {
        if (part.type !== 'ui_component') return true
        const data = part.data
        if (data && typeof data === 'object' && 'type' in data) {
          // location_entities drive the right-panel map only — not chat UI.
          // In-chat `map` cards stay in the transcript.
          return data.type !== 'location_entities'
        }
        return true
      }),
    }))
  }, [filteredMessages])

  const lastAssistantMessage = useMemo(() => {
    return filteredMessages.findLast(m => m.role === 'assistant')
  }, [filteredMessages])

  const hasUserMessage = useMemo(() => {
    return filteredMessages.some(m => m.role === 'user')
  }, [filteredMessages])

  const lastMessageIsAssistant = filteredMessages.length > 0 && filteredMessages[filteredMessages.length - 1].role === 'assistant'
  const shouldShowAssistantActions = !!lastAssistantMessage && lastMessageIsAssistant && !isLoading && !isStreaming
  const isRegenerationCompatible = isTripPlannerRegenerationCompatible(lastAssistantMessage?.assistantTurnPolicySnapshot)
  // Regenerating a built WhatsApp handoff trip would rebuild it; a failed one keeps Retry because a new chat would carry only its recent messages.
  const isWhatsAppHandoffReply = useMemo(() => {
    const lastUserMessage = filteredMessages.findLast(m => m.role === 'user')
    return lastUserMessage !== undefined &&
      hasContextLabel(getTextFromParts(lastUserMessage.parts), WHATSAPP_HANDOFF_CONTEXT_LABEL)
  }, [filteredMessages])
  const failedMessageId = lastAssistantMessage?.generationStatus === 'FAILED' ? lastAssistantMessage.id : undefined
  const hasRetriedLatestFailedMessage = failedMessageId !== undefined &&
    (retriedFailedMessageId === failedMessageId || hasRetriedFailedMessage(failedMessageId))

  const hasMultiChoiceSelection = activeMultiChoiceSelection.length > 0
  const hasPrefilledSelection = prefilledSelectionTags.length > 0
  const hasMobileSelection = mobileSelectionText !== undefined
  const hasFiles = files.length > 0
  const hasMessageContent = messageInputValue.trim() !== '' || hasMultiChoiceSelection || hasPrefilledSelection || hasMobileSelection || hasFiles
  const showStopButton = isStreaming || isStreamOpen
  const isChatBusy = isLoading || showStopButton || isFlightCartMutationPending
  const disableSubmit = isChatBusy || !hasMessageContent

  const allSelectedTags = useMemo(() => {
    const tags = [...prefilledSelectionTags, ...activeMultiChoiceSelection]
    if (mobileSelectionText) {
      tags.push(mobileSelectionText)
    }
    if (offerContextTag) {
      tags.push(offerContextTag.label)
    }
    return [...new Set(tags)]
  }, [activeMultiChoiceSelection, mobileSelectionText, offerContextTag, prefilledSelectionTags])

  const handleRemoveSelectedTag = useCallback((tag: string) => {
    if (prefilledSelectionTags.includes(tag)) {
      onRemovePrefilledSelectionTag()
      if (activeMultiChoiceSelection.includes(tag)) {
        handleRemoveTag(tag)
      }
      return
    }

    handleRemoveTag(tag)
  }, [activeMultiChoiceSelection, handleRemoveTag, onRemovePrefilledSelectionTag, prefilledSelectionTags])

  // ── Chat error effect ──

  useEffect(() => {
    if (isChatError && chatId !== undefined) {
      const errorStatus = chatError && typeof chatError === 'object' && 'status' in chatError && typeof chatError.status === 'number'
        ? chatError.status
        : undefined
      showSnackbar(
        getChatErrorMessage(intl, errorStatus),
        'critical',
        { heading: getChatErrorHeading(intl) },
      )
      navigateToNewChatRoute()
    }
  }, [isChatError, chatId, chatError, navigateToNewChatRoute, intl])

  // ── Hooks ──

  const { copiedRecently, handleCopyResponse } = useClipboard({ lastAssistantMessage, isDevMode })

  const handleFeedbackSubmitted = useCallback((messageId: string, reaction: MessageFeedbackReaction) => {
    setUserFeedbackMap(prev => ({
      ...prev,
      [messageId]: reaction,
    }))
    // messageId is always lastAssistantMessage.id here (useFeedback only ever submits with
    // lastAssistantMessageId), so the message is always found -- the guard just keeps the return
    // type honest without asserting. Looked up from `messages`, not `filteredMessages`, and reads
    // the server-assigned messageSeq directly rather than recomputing a position by counting --
    // filteredMessages strips messages by messageType in production but not in dev, so a
    // recomputed position could drift from whatever numbering the model's own transcript implies
    // (PR review finding on #37336); messageSeq can't drift, since it's not derived from this list
    // at all.
    const targetMessage = messages.find(message => message.id === messageId)
    // Unlike a state-mutating action (e.g. default_transport_switched's !isInspecting guard),
    // feedback is a proxy for genuine customer sentiment, not an objective change that's equally
    // "real" regardless of who clicked it -- so this suppresses on any staff-driven view of this
    // chat, not just the narrower isInspecting sub-case. isSpoofed (staff impersonating the whole
    // account) and canStaffRewind (the separate staff chat-viewing tool, independent of spoofing)
    // are both reachable without the other being true.
    if (targetMessage?.messageSeq !== undefined && !isSpoofed && !canStaffRewind) {
      eventBus.dispatch({
        type: 'trip_planner_surface_activity',
        entries: [buildMessageFeedbackActivityEntry(reaction, targetMessage.messageSeq)],
      })
    }
  }, [setUserFeedbackMap, messages, eventBus, isSpoofed, canStaffRewind])

  const { feedbackSubmitted, handleThumbsUp, handleThumbsDown } = useFeedback({
    chatId,
    lastAssistantMessageId: lastAssistantMessage?.id,
    existingFeedback: userFeedbackMap,
    onFeedbackSubmitted: handleFeedbackSubmitted,
  })

  // ── Callbacks ──

  const focusChatInput = useCallback(() => {
    chatInputRef.current?.focus()
  }, [chatInputRef])

  const {
    clearChatInputContextAfterSubmit,
    clearChatInputContextManually,
  } = useDrawerAskAIComposerContext({
    chatInputContext,
    messageInputValue,
    setChatInputContext,
    setMessageInputValue,
    focusChatInput,
  })

  // Landing point for the Ask AI context comet: the inline-start edge of the context chip.
  const getContextTraceTarget = useCallback((): TracePoint | null => {
    const contextBar = contextBarRef.current
    const rect = contextBar?.getBoundingClientRect()
    if (!contextBar || !rect || rect.height === 0) return null
    const x = getElementTextDirection(contextBar) === 'rtl' ? rect.right - 32 : rect.left + 32
    return { x, y: rect.top + rect.height * 0.42 }
  }, [])

  const handleContextTraceDone = useCallback(() => {
    setContextTrace(undefined)
  }, [])

  const handleClearChatInputContext = useCallback(() => {
    clearChatInputContextManually()
  }, [clearChatInputContextManually])

  const handleRegenerateResponse = useCallback(async() => {
    if (isLoading) return
    if (!lastAssistantMessage) return
    if (!isRegenerationCompatible) return
    const lastUserMessage = messages.findLast((message) => message.role === 'user')
    if (!lastUserMessage) return
    setFollowTail(false)
    return streamingRegenerate()
  }, [isLoading, isRegenerationCompatible, lastAssistantMessage, messages, streamingRegenerate, setFollowTail])

  const handleRetryFailedMessage = useCallback(() => {
    if (!failedMessageId || isStreaming || !isRegenerationCompatible) return
    try {
      window.sessionStorage.setItem(`${FAILED_MESSAGE_RETRY_STORAGE_PREFIX}${failedMessageId}`, '1')
    } catch { /* Keep the retry available for this page session. */ }
    setRetriedFailedMessageId(failedMessageId)
    handleRegenerateResponse().catch(() => { /* Regeneration reports unexpected errors in a snackbar. */ })
  }, [failedMessageId, isStreaming, isRegenerationCompatible, handleRegenerateResponse])

  const handleContinueFailedMessageInNewChat = useCallback(async() => {
    if (!failedMessageId || isStreaming || chatId === undefined) return
    try {
      let currentItinerary = itineraryData
      let itineraryFetchFailed = false
      try {
        currentItinerary = await getItinerary(chatId, currentRegionCode, accessToken ?? undefined)
      } catch {
        itineraryFetchFailed = true
      }
      const prompt = buildFailedChatRecoveryPrompt(currentItinerary, messages, failedMessageId, itineraryFetchFailed && !currentItinerary)
      navigateTo(CHAT_TRIP_PLANNER_NEW_PATH, buildStagedChatPromptQuery(prompt))
    } catch {
      showSnackbar(intl.formatMessage({
        id: 'cKC67S5_VqVianxZ-Fzd',
        defaultMessage: 'We could not prepare a new chat. Please try again.',
        description: 'Error shown when a failed trip planner chat cannot stage its recovery prompt',
      }), 'critical')
    }
  }, [failedMessageId, isStreaming, chatId, currentRegionCode, accessToken, itineraryData, messages, navigateTo, intl])

  const handleScheduleCallback = useScheduleCallbackChatAction({
    submitMessage,
  })

  const handleSubmitWithChatInputContext = useCallback((message: string, options?: OrderLinkOptions) => {
    if (chatInputContext?.message) {
      const messageWithContext = appendContextTag(
        message,
        chatInputContext.message,
        chatInputContext.label,
      )

      clearChatInputContextAfterSubmit(chatInputContext)

      return handleSubmit(messageWithContext, options)
    }
    return handleSubmit(message, options)
  }, [chatInputContext, clearChatInputContextAfterSubmit, handleSubmit])

  const handleAddBookings = useCallback<AddBookingsSend>((message, { orderIds, bookingItemIds }) => {
    return addBookingItems(() => handleSubmitWithChatInputContext(message, { orderIds }), bookingItemIds)
  }, [addBookingItems, handleSubmitWithChatInputContext])

  // The modal renders outside the chat runtime, so the submit lives here where the chat session is known.
  const { submitBookings } = useAddBookingsSubmit({ chatId, isLoggedIn, onAddBookings: handleAddBookings })

  const handleAddBookingClick = useCallback(() => {
    showModal(
      <AddBookingModal
        onSubmitBookings={submitBookings}
        disabledBookingItemIds={addedBookingItemIds}
        isSubmitLocked={isStreaming}
      />,
    )
  }, [addedBookingItemIds, isStreaming, showModal, submitBookings])

  // ── History overlay scroll forwarding ──

  const handleHistoryOverlayWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    if (!messagesWrapperRef.current) {
      return
    }
    event.preventDefault()
    messagesWrapperRef.current.scrollTop += event.deltaY
  }, [messagesWrapperRef])

  const handleHistoryOverlayTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    const firstTouch = event.touches[0]
    if (!firstTouch) {
      return
    }
    historyOverlayTouchStartYRef.current = firstTouch.clientY
  }, [])

  const handleHistoryOverlayTouchMove = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    if (!messagesWrapperRef.current || historyOverlayTouchStartYRef.current === undefined) {
      return
    }

    const touch = event.touches[0]
    if (!touch) {
      return
    }

    const deltaY = historyOverlayTouchStartYRef.current - touch.clientY
    if (deltaY !== 0) {
      messagesWrapperRef.current.scrollTop += deltaY
      historyOverlayTouchStartYRef.current = touch.clientY
    }
    event.preventDefault()
  }, [messagesWrapperRef])

  const handleHistoryOverlayTouchEnd = useCallback(() => {
    historyOverlayTouchStartYRef.current = undefined
  }, [])

  // ── ChatMessageActionsProvider callbacks ──

  const handleHotelMapViewClick = useCallback((data: HotelMapViewData) => {
    eventBus.dispatch({ type: 'show_hotel_on_map', data })
  }, [eventBus])

  const handleOpenHotelListing = useCallback((data: ListingSearchDrawerRequest) => {
    eventBus.dispatch({ type: 'open_listing_search_drawer', ...data })
  }, [eventBus])

  const handleExperienceMapViewClick = useCallback((data: ExperienceMapViewData) => {
    eventBus.dispatch({ type: 'show_experience_on_map', data })
  }, [eventBus])

  const handleOfferClick = useCallback((data: { offerId: string; searchParams?: OfferClickSearchParams; offerType?: string }) => {
    eventBus.dispatch({ type: 'open_offer_drawer', ...data })
  }, [eventBus])

  const handlePlaceClick = useCallback((data: { googlePlaceId: string; placeName: string }) => {
    eventBus.dispatch({ type: 'open_place_drawer', ...data })
    eventBus.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildPlaceDrawerOpenedActivityEntry(data.placeName, 'trip_planner.chat.place_link', data.googlePlaceId)],
    })
  }, [eventBus])

  const handlePlaceSearchClick = useCallback((data: { searchQuery: string; placeName: string }) => {
    eventBus.dispatch({ type: 'open_place_search_drawer', ...data })
  }, [eventBus])

  const handleChatMapEnlarge = useCallback((data: ChatMapOnMapData) => {
    eventBus.dispatch({ type: 'show_chat_map_on_map', data })
  }, [eventBus])

  const handleLocationInfoClick = useCallback((data: LocationInfoData) => {
    eventBus.dispatch({ type: 'open_location_drawer', data })
    eventBus.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildLocationSuggestionOpenedActivityEntry(data)],
    })
  }, [eventBus])

  // ── Event bus subscriptions ──

  useEventSubscription('ask_ai_for_day', (event) => {
    eventBus.dispatch({ type: 'focus_floating_ask_ai' })
    if (deferAskAIHighlightToLocalComposer) return

    setChatInputContext(buildAskAIContextForDay(intl, event))
    focusChatInput()
  })

  useEventSubscription('ask_ai_for_item', (event) => {
    eventBus.dispatch({ type: 'focus_floating_ask_ai' })
    if (deferAskAIHighlightToLocalComposer) return

    setChatInputContext(buildAskAIContextForItem(intl, event))
    focusChatInput()
  })

  useEventSubscription('ask_ai_for_list_row', (event) => {
    eventBus.dispatch({ type: 'focus_floating_ask_ai' })
    if (deferAskAIHighlightToLocalComposer) return

    setChatInputContext(buildAskAIContextForListRow(intl, event))
    focusChatInput()
    if (event.origin) {
      traceKeyRef.current += 1
      setContextTrace({ origin: event.origin, key: traceKeyRef.current })
    }
  })

  useEventSubscription('reset_chat_state', () => {
    setMessageInputValue('')
    setChatInputContext(undefined)
  })

  // ── i18n ──

  const chatInputPlaceholder = chatInputContext?.placeholder
  const messagesErrorText = intl.formatMessage({
    id: 'zNUAhjKCUvuU-8zm4DQm',
    defaultMessage: 'Something went wrong displaying messages.',
    description: 'Fallback error message shown if trip planner chat messages fail to render',
  })

  const renderMessagesError = useCallback(() => (
    <BodyText variant="medium" colour="critical">{messagesErrorText}</BodyText>
  ), [messagesErrorText])

  const chatPanelTopSlot = useMemo(() => (
    <>
      <ChatSurfaceSwitcher chatId={chatId} disabled={isStreaming} />
      <SalesAgentSummaryPanel
        chatId={chatId}
        enabled={shouldShowSalesSummary}
        initiallyCollapsed={shouldStartSalesSummaryCollapsed}
      />
    </>
  ), [chatId, isStreaming, shouldShowSalesSummary, shouldStartSalesSummaryCollapsed])

  const guestLoginPrompt = useMemo(() => (isLoggedIn ? undefined : <GuestChatLoginPrompt />), [isLoggedIn])

  // ── Render ──

  const inputContent = (
    <InputWrapper
      className={clsx({
        'has-context-bar': !!chatInputContext,
        'flush-bottom': fitAvailableHeight,
      })}
    >
      <div ref={contextBarRef}>
        {chatInputContext && (
          <ChatInputContextBar
            label={chatInputContext.label}
            onClose={handleClearChatInputContext}
          />
        )}
      </div>
      {contextTrace && (
        <AiContextTrace
          key={contextTrace.key}
          origin={contextTrace.origin}
          getTarget={getContextTraceTarget}
          onDone={handleContextTraceDone}
        />
      )}
      <ChatMessageInput
        onSubmit={handleSubmitWithChatInputContext}
        value={messageInputValue}
        onChange={setMessageInputValue}
        disableSubmit={disableSubmit}
        isStreaming={isStreaming}
        showStopButton={showStopButton}
        files={files}
        onFilesChange={setFiles}
        selectedTags={allSelectedTags}
        onAddBookingClick={handleAddBookingClick}
        showAddBookingAction={isLoggedIn}
        onRemoveTag={handleRemoveSelectedTag}
        inputRef={chatInputRef}
        placeholder={chatInputPlaceholder}
        autoFocus={autoFocusChatInput}
        dismissKeyboardOnSubmit={dismissKeyboardOnSubmit}
        compactDisclaimer={compactDisclaimer}
        chatId={chatId}
        voicePrompt="chatNudge"
        isVoicePromptReady={isComposerVisible && !isChatBusy && !failedMessageId}
        overlayVoicePrompt={!!chatInputContext || fitAvailableHeight}
      />
    </InputWrapper>
  )

  return (
    <ChatMapLiveSlotProvider>
      <ChatMessageActionsProvider
        onMultiChoiceSelectionChange={handleMultiChoiceSelectionChange}
        onOfferClick={handleOfferClick}
        onOfferHover={onCardHover}
        onHotelMapViewClick={enableMapActions ? handleHotelMapViewClick : noop}
        onOpenHotelListing={handleOpenHotelListing}
        onExperienceMapViewClick={enableMapActions ? handleExperienceMapViewClick : noop}
        onPlaceClick={handlePlaceClick}
        onPlaceSearchClick={handlePlaceSearchClick}
        onChatMapEnlarge={handleChatMapEnlarge}
        onLocationInfoClick={handleLocationInfoClick}
        onScheduleCallback={handleScheduleCallback}
        onSendMessage={handleSubmit}
        onAuthenticatedFollowUp={sendPostAuthMessage}
        itineraryData={itineraryData}
        isDevMode={isDevMode}
      >
        <ChatFileDropArea files={files} onFilesChange={setFiles}>
          <ChatPanel
            messagesWrapperRef={messagesWrapperRef}
            onScroll={handleMessagesScroll}
            isOverlay={false}
            showHistoryOverlay={false}
            onHistoryWheel={handleHistoryOverlayWheel}
            onHistoryTouchStart={handleHistoryOverlayTouchStart}
            onHistoryTouchMove={handleHistoryOverlayTouchMove}
            onHistoryTouchEnd={handleHistoryOverlayTouchEnd}
            inputSlot={inputContent}
            isAtBottom={isAtBottom}
            hasContentOutOfView={hasContentOutOfView}
            onScrollToBottomButtonClick={enableFollowAndScrollToBottom}
            dynamicSpacerHeight={dynamicSpacerHeight}
            fitAvailableHeight={fitAvailableHeight}
            topSlot={chatPanelTopSlot}
          >
            <ErrorBoundary fallback={renderMessagesError}>
              <MessagesList
                messages={messagesWithoutLocationEntities}
                chatId={chatId}
                isLoading={isListLoading}
                isUpdatingItinerary={isUpdatingItinerary}
                activeToolLoadingKey={activeToolLoadingKey}
                isOnline={isOnline}
                reasoningSummaries={reasoningSummaries}
                toolCallInfo={toolCallInfo}
                loadingStatus={loadingState.status}
                showAssistantActions={shouldShowAssistantActions}
                isPrivateChat={isSpoofed && hiddenFromCustomer}
                showRegenerateButton={isRegenerationCompatible && !(isWhatsAppHandoffReply && lastAssistantMessage?.generationStatus === 'COMPLETED')}
                failedMessageId={failedMessageId}
                hasRetriedFailedMessage={(hasRetriedLatestFailedMessage && !isWhatsAppHandoffReply) || !isRegenerationCompatible}
                onRetryFailedMessage={handleRetryFailedMessage}
                onContinueFailedMessageInNewChat={handleContinueFailedMessageInNewChat}
                isStreaming={isStreaming}
                onRegenerate={handleRegenerateResponse}
                onCopy={handleCopyResponse}
                disableRegenerateButton={isLoading || !hasUserMessage || !isRegenerationCompatible}
                disableCopyButton={!lastAssistantMessage || isLoading}
                copiedRecently={copiedRecently}
                onThumbsUp={handleThumbsUp}
                onThumbsDown={handleThumbsDown}
                feedbackSubmitted={feedbackSubmitted}
                externalSelection={activeMultiChoiceSelection}
                FlightSearchDrawerComponent={FlightSearchDrawer}
                CartComponent={ChatCart}
                showAddedItineraryItemLink={showAddedItineraryItemLink}
                thirdAssistantResponseSlot={guestLoginPrompt}
              />
            </ErrorBoundary>
          </ChatPanel>
        </ChatFileDropArea>
      </ChatMessageActionsProvider>
    </ChatMapLiveSlotProvider>
  )
}

export default ChatContainer
